import type { Patient } from 'app/generated/graphql'
import type { ContactJson } from 'app/types/entities'
import type { Family, FamilyPerson, PrimaryReason, Recipient, WorkspaceFamilyInput } from 'app/types/related-contacts'

export const OPEN_LEAD_STATUSES: readonly string[] = [`Needs Action`, `In Progress`]

export function recipientFor(person: FamilyPerson, family: Family): Recipient {
  const primary = family.primary
  const reachable = primary && primary.id !== person.id ? primary : undefined
  if ((person.age ?? 18) < 18) {
    return reachable ? { kind: `primary`, person: reachable } : { kind: `none`, reason: `No adult to contact` }
  }
  if (person.ownNumber || !reachable) return { kind: `self`, person }
  return { kind: `primary`, person: reachable }
}

export function ageOn(birthDate: string | Date | null | undefined, now: Date): number | undefined {
  if (!birthDate) return undefined
  const born = new Date(birthDate)
  const age = now.getUTCFullYear() - born.getUTCFullYear()
  const hadBirthday =
    now.getUTCMonth() > born.getUTCMonth() ||
    (now.getUTCMonth() === born.getUTCMonth() && now.getUTCDate() >= born.getUTCDate())
  return hadBirthday ? age : age - 1
}

/** `locationId:pmsPatientId` of every guarantor who is not the patient themselves. */
export function guarantorKeys(patients: Patient[]): string[] {
  return patients
    .filter((p) => p.guarantorId && p.guarantorId !== p.pmsPatientId)
    .map((p) => `${p.locationId}:${p.guarantorId}`)
}

/**
 * Groups active leads into families: a lead's primary contact is its adult PMS guarantor, else the
 * adult who first appeared on its cell number in the same location. A minor with neither gets a
 * family with no primary contact.
 */
export function buildWorkspaceFamilies(input: WorkspaceFamilyInput): Family[] {
  const { leads, patientsById, guarantorPatients, guarantorContactByPatientId, contactsByPhone, now } = input
  const guarantorPatientByKey = new Map(guarantorPatients.map((p) => [`${p.locationId}:${p.pmsPatientId}`, p]))

  const toPerson = (contact: ContactJson): FamilyPerson => ({
    id: contact.id!,
    name: contact.name ?? `Unknown`,
    phone: contact.cellPhone ?? undefined,
    age: ageOn(contact.birthDate, now),
    leadStatus: OPEN_LEAD_STATUSES.includes(contact.leadStatus ?? ``) ? (contact.leadStatus ?? undefined) : undefined,
    openTreatment: contact.unbookedTreatmentValue ?? 0,
    ownNumber: true,
    contact,
  })
  const isAdultContact = (contact: ContactJson) => (ageOn(contact.birthDate, now) ?? 18) >= 18

  const families = new Map<string, Family>()
  for (const lead of leads) {
    const person = toPerson(lead)
    const guarantorContact = (lead.patientIds ?? [])
      .map((id) => patientsById.get(id))
      .flatMap((p) =>
        p && p.guarantorId !== p.pmsPatientId ? [guarantorPatientByKey.get(`${p.locationId}:${p.guarantorId}`)] : [],
      )
      .map((g) => (g ? guarantorContactByPatientId.get(g.id) : undefined))
      .find((c) => c && c.id !== lead.id && isAdultContact(c))

    const phoneOwner = lead.cellPhone
      ? (contactsByPhone.get(`${lead.locationId}:${lead.cellPhone}`) ?? [])
          .filter(isAdultContact)
          .sort((a, b) => new Date(a.createdDate ?? 0).getTime() - new Date(b.createdDate ?? 0).getTime())[0]
      : undefined

    const primaryContact = guarantorContact ?? (phoneOwner && phoneOwner.id !== lead.id ? phoneOwner : undefined)
    const reason: PrimaryReason = guarantorContact ? `guarantor` : `phone owner`

    if (!primaryContact) {
      const noAdult = (person.age ?? 18) < 18
      const id = noAdult ? `none:${lead.id}` : lead.id!
      const family = families.get(id) ?? { id, primary: noAdult ? undefined : { ...person, reason }, members: [] }
      if (noAdult) family.members.push(person)
      families.set(id, family)
      continue
    }

    const family = families.get(primaryContact.id!) ?? {
      id: primaryContact.id!,
      primary: { ...toPerson(primaryContact), reason },
      members: [],
    }
    if (family.primary?.id === lead.id) family.primary = { ...person, reason }
    else family.members.push({ ...person, ownNumber: lead.cellPhone !== primaryContact.cellPhone })
    families.set(family.id, family)
  }
  return [...families.values()]
}
